import { sb } from '../supabase.ts';
import { escapeHtml } from '../main.ts';
import { IMGBB_API_KEY } from '../config.ts';

export async function renderTeacherEditPage(app: HTMLElement) {
  const tableName = 'teacher_database';
  const params = new URLSearchParams(window.location.search);
  const iid = params.get('iid');
  const mode = params.get('mode') || 'view'; // 'view' or 'edit'

  if (!iid) {
    app.innerHTML = '<div class="container"><h1>Error</h1><p>No IID provided.</p></div>';
    return;
  }

  const fieldGroups = [
    { title: 'Identity', fields: ['iid', 'teacher_name_en', 'teacher_name_bn', 'pp_photo', 'nid_photo'] },
    { title: 'Designation & Sector', fields: ['sector', 'designation_en', 'designation_bn', 'subject_en', 'subject_bn', 'index_number', 'pds_number', 'pay_code', 'basic', '1st_mpo'] },
    { title: 'Parents', fields: ['father_name_en', 'father_name_bn', 'mother_name_en', 'mother_name_bn'] },
    { title: 'Personal', fields: ['date_of_birth_en', 'date_of_birth_bn', 'gender_en', 'gender_bn', 'religion_en', 'religion_bn', 'nid_number_en', 'nid_number_bn', 'birth_certificate_number'] },
    { title: 'Contact', fields: ['mobile_number_en', 'mobile_number_bn', 'teacher_email_id'] },
    { title: 'Banking', fields: ['bank_account_number', 'bank_account_number2'] },
    { title: 'Addresses', fields: ['village_en', 'village_bn', 'post_office_en', 'post_office_bn', 'upazilla_en', 'upazilla_bn', 'district_en', 'district_bn', 'home_district'] },
    { title: 'Service', fields: ['this_school_join_date', 'join_date', 'created_at'] },
    { title: 'Additional Documents', fields: ['url_1', 'url_2', 'url_3'] },
  ];

  const labels: Record<string, string> = {
    iid: 'IID', teacher_name_en: 'Teacher Name (EN)', teacher_name_bn: 'Teacher Name (BN)', pp_photo: 'Profile Photo URL', nid_photo: 'NID Photo URL',
    sector: 'Sector', designation_en: 'Designation (EN)', designation_bn: 'Designation (BN)', subject_en: 'Subject (EN)', subject_bn: 'Subject (BN)',
    index_number: 'Index Number', pds_number: 'PDS Number', pay_code: 'Pay Code', basic: 'Basic', '1st_mpo': '1st MPO',
    father_name_en: 'Father Name (EN)', father_name_bn: 'Father Name (BN)', mother_name_en: 'Mother Name (EN)', mother_name_bn: 'Mother Name (BN)',
    date_of_birth_en: 'DOB (EN)', date_of_birth_bn: 'DOB (BN)', gender_en: 'Gender (EN)', gender_bn: 'Gender (BN)', religion_en: 'Religion (EN)', religion_bn: 'Religion (BN)',
    nid_number_en: 'NID (EN)', nid_number_bn: 'NID (BN)', birth_certificate_number: 'Birth Certificate No', mobile_number_en: 'Mobile (EN)', mobile_number_bn: 'Mobile (BN)',
    teacher_email_id: 'Email', bank_account_number: 'Bank A/C 1', bank_account_number2: 'Bank A/C 2',
    village_en: 'Village (EN)', village_bn: 'Village (BN)', post_office_en: 'Post Office (EN)', post_office_bn: 'Post Office (BN)',
    upazilla_en: 'Upazilla (EN)', upazilla_bn: 'Upazilla (BN)', district_en: 'District (EN)', district_bn: 'District (BN)', home_district: 'Home District',
    this_school_join_date: 'This School Join Date', join_date: 'Join Date', created_at: 'Created At',
    url_1: 'Document URL 1', url_2: 'Document URL 2', url_3: 'Document URL 3'
  };

  const readonlyFields = new Set(['iid', 'created_at']);

  app.innerHTML = `
<style>
  .edit-wrap { background: #f8f9fa; min-height: 100vh; padding-bottom: 60px; font-family: system-ui, -apple-system, sans-serif; }
  .sticky-header { position: sticky; top: 0; background: #fff; border-bottom: 1px solid #e5e7eb; z-index: 100; padding: 12px 0; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
  .group-card { background: #fff; border-radius: 12px; border: 1px solid #e5e7eb; margin-bottom: 24px; overflow: hidden; box-shadow: 0 4px 6px rgba(0,0,0,0.02); }
  .group-header { background: #fff; padding: 12px 20px; border-bottom: 1px solid #e5e7eb; border-left: 4px solid #ff6b35; }
  .group-header h3 { margin: 0; font-size: 15px; font-weight: 700; color: #1e293b; text-transform: uppercase; letter-spacing: 0.5px; }
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; padding: 20px; }
  @media (max-width: 768px) { .grid-2 { grid-template-columns: 1fr; } }
  .field-item { display: flex; flex-direction: column; gap: 6px; }
  .field-item label { font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
  .input-wrap { display: flex; flex-direction: column; gap: 6px; }
  .input-wrap input, .input-wrap select, .input-wrap textarea { padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; background: #fff; transition: all 0.2s; }
  .input-wrap input:focus { outline: none; border-color: #ff6b35; box-shadow: 0 0 0 3px rgba(255,107,53,0.1); }
  .input-wrap input.readonly { background: #f1f5f9; cursor: not-allowed; }
  .field-display { padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 14px; background: #f8fafc; color: #1e293b; min-height: 40px; display: flex; align-items: center; }
  .field-display.empty { color: #94a3b8; font-style: italic; }
  .photo-banner { background: #fff; padding: 30px; text-align: center; border-radius: 12px; border: 1px solid #e5e7eb; margin-bottom: 24px; position: relative; }
  .photo-banner img { width: 140px; height: 180px; object-fit: cover; border-radius: 12px; border: 4px solid #fff; box-shadow: 0 10px 25px rgba(0,0,0,0.1); background: #f3f4f6; cursor: pointer; }
  .photo-banner h2 { margin: 15px 0 5px; font-size: 24px; font-weight: 800; color: #1e293b; }
  .iid-tag { background: #f1f5f9; color: #64748b; padding: 4px 12px; border-radius: 20px; font-size: 13px; font-weight: 600; }
  .actions-bar { display: flex; justify-content: flex-end; gap: 12px; margin-top: 30px; background: #fff; padding: 20px; border-radius: 12px; border: 1px solid #e5e7eb; position: sticky; bottom: 20px; box-shadow: 0 -4px 12px rgba(0,0,0,0.05); }
  .btn { padding: 10px 24px; border-radius: 8px; font-weight: 600; cursor: pointer; border: 1px solid transparent; transition: all 0.2s; }
  .btn.primary { background: #ff6b35; color: #fff; }
  .btn.primary:hover { background: #e85a2a; }
  .btn.outline { background: #fff; border-color: #d1d5db; color: #4b5563; }
  .btn.outline:hover { background: #f9fafb; border-color: #9ca3af; }
  .thumb-wrap { margin-top: 8px; display: flex; align-items: center; gap: 10px; }
  .row-thumb { width: 40px; height: 50px; object-fit: cover; border-radius: 6px; border: 1px solid #e2e8f0; cursor: pointer; }
  .upload-btn { padding: 4px 10px; font-size: 11px; background: #10b981; color: white; border: none; border-radius: 4px; cursor: pointer; }
  #photo-preview { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: #fff; border: 2px solid #ddd; border-radius: 8px; padding: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.35); z-index: 10001; display: none; pointer-events: none; }
  #photo-preview img { max-width: 500px; max-height: 600px; object-fit: contain; }
</style>

<div class="edit-wrap">
  <div class="sticky-header">
    <div class="container" style="display:flex; justify-content:space-between; align-items:center;">
      <button id="backBtn" class="btn outline small">← Back</button>
      <div style="display:flex; gap:10px;">
        ${mode === 'view' ? `<button id="switchToEdit" class="btn primary small">Edit Profile</button>` : ''}
        <div id="globalStatus" class="pill" style="background:#f0f9ff; color:#0366d6; padding:6px 12px; border-radius:20px; font-size:12px; font-weight:600;">Loading...</div>
      </div>
    </div>
  </div>

  <div class="container" style="max-width: 900px; margin-top: 24px;">
    <div class="photo-banner">
      <img id="teacherPhoto" src="" alt="Photo">
      <h2 id="teacherNameHeader">Loading...</h2>
      <span class="iid-tag">Teacher IID: ${iid}</span>
    </div>

    <div id="groupsContainer"></div>

    <div class="actions-bar" style="${mode === 'view' ? 'display:none' : ''}">
      <button id="cancelBtn" class="btn outline">Cancel</button>
      <button id="saveBtn" class="btn primary">Save Teacher Details</button>
    </div>
  </div>
</div>
<div id="photo-preview"><img src="" alt="preview"/></div>
  `;

  const groupsContainer = document.getElementById('groupsContainer')!;
  const globalStatus = document.getElementById('globalStatus')!;
  const teacherPhoto = document.getElementById('teacherPhoto') as HTMLImageElement;
  const teacherNameHeader = document.getElementById('teacherNameHeader')!;
  const backBtn = document.getElementById('backBtn')!;
  
  if (mode === 'view') {
    const switchToEdit = document.getElementById('switchToEdit');
    if (switchToEdit) {
      switchToEdit.onclick = () => {
        window.location.href = `/teacher_edit?iid=${iid}&mode=edit`;
      };
    }
  }

  backBtn.onclick = () => {
    if (mode === 'edit' && !confirm('Discard unsaved changes?')) return;
    window.location.href = '/teacher';
  };

  const { data, error } = await sb.from(tableName).select('*').eq('iid', iid).single();
  if (error) {
    globalStatus.textContent = 'Error: ' + error.message;
    globalStatus.style.color = 'red';
    return;
  }

  teacherPhoto.src = data.pp_photo || 'https://via.placeholder.com/140x180?text=No+Photo';
  teacherNameHeader.textContent = data.teacher_name_en || 'Teacher Profile';
  
  fieldGroups.forEach(group => {
    const card = document.createElement('div');
    card.className = 'group-card';
    card.innerHTML = `
      <div class="group-header"><h3>${group.title}</h3></div>
      <div class="grid-2"></div>
    `;
    const grid = card.querySelector('.grid-2')!;
    
    group.fields.forEach(field => {
      const val = data[field] ?? '';
      const isReadonly = readonlyFields.has(field);
      const isUrl = field.includes('url') || field.includes('photo');
      
      const item = document.createElement('div');
      item.className = 'field-item';
      
      if (mode === 'view') {
        item.innerHTML = `
          <label>${labels[field] || field}</label>
          <div class="field-display ${!val ? 'empty' : ''}">${escapeHtml(val) || '—'}</div>
          ${isUrl && val ? `
            <div class="thumb-wrap">
              <img src="${val}" class="row-thumb hover-preview" data-url="${val}">
              <a href="${val}" target="_blank" style="font-size:11px; color:#ff6b35;">Open Original ↗</a>
            </div>` : ''}
        `;
      } else {
        item.innerHTML = `
          <label>${labels[field] || field}</label>
          <div class="input-wrap">
            <input type="text" name="${field}" value="${escapeHtml(val)}" 
              class="${isReadonly ? 'readonly' : ''}" 
              ${isReadonly ? 'readonly' : ''}>
            ${isUrl ? `
              <div class="thumb-wrap">
                ${val ? `<img src="${val}" class="row-thumb hover-preview" data-url="${val}">` : ''}
                <button class="upload-btn" data-field="${field}">📷 Upload</button>
                <input type="file" class="hidden-file" data-field="${field}" style="display:none" accept="image/*">
              </div>` : ''}
          </div>
        `;
      }
      grid.appendChild(item);
    });
    groupsContainer.appendChild(card);
  });

  // Handle remaining fields
  const allGroupFields = fieldGroups.flatMap(g => g.fields);
  const otherFields = Object.keys(data).filter(k => !allGroupFields.includes(k) && k !== 'created_at' && k !== 'iid');
  
  if (otherFields.length > 0) {
    const card = document.createElement('div');
    card.className = 'group-card';
    card.innerHTML = `
      <div class="group-header"><h3>Other Information</h3></div>
      <div class="grid-2"></div>
    `;
    const grid = card.querySelector('.grid-2')!;
    otherFields.forEach(field => {
      const val = data[field] ?? '';
      const isUrl = field.includes('url') || field.includes('photo');
      const item = document.createElement('div');
      item.className = 'field-item';
      
      if (mode === 'view') {
        item.innerHTML = `
          <label>${field.replace(/_/g, ' ')}</label>
          <div class="field-display ${!val ? 'empty' : ''}">${escapeHtml(val) || '—'}</div>
          ${isUrl && val ? `
            <div class="thumb-wrap">
              <img src="${val}" class="row-thumb hover-preview" data-url="${val}">
              <a href="${val}" target="_blank" style="font-size:11px; color:#ff6b35;">Open Original ↗</a>
            </div>` : ''}
        `;
      } else {
        item.innerHTML = `
          <label>${field.replace(/_/g, ' ')}</label>
          <div class="input-wrap">
            <input type="text" name="${field}" value="${escapeHtml(val)}">
            ${isUrl ? `
              <div class="thumb-wrap">
                ${val ? `<img src="${val}" class="row-thumb hover-preview" data-url="${val}">` : ''}
                <button class="upload-btn" data-field="${field}">📷 Upload</button>
                <input type="file" class="hidden-file" data-field="${field}" style="display:none" accept="image/*">
              </div>` : ''}
          </div>
        `;
      }
      grid.appendChild(item);
    });
    groupsContainer.appendChild(card);
  }

  globalStatus.textContent = mode === 'view' ? 'View Mode' : 'Edit Mode';

  if (mode === 'edit') {
    const saveBtn = document.getElementById('saveBtn')!;
    const cancelBtn = document.getElementById('cancelBtn')!;
    
    cancelBtn.onclick = () => {
      if (confirm('Discard changes?')) window.location.href = `/teacher_edit?iid=${iid}&mode=view`;
    };

    saveBtn.onclick = async () => {
      saveBtn.textContent = 'Saving...';
      const inputs = groupsContainer.querySelectorAll('input[type="text"]');
      const updates: any = {};
      inputs.forEach((inp: any) => {
        if (!readonlyFields.has(inp.name)) {
          updates[inp.name] = inp.value.trim() || null;
        }
      });

      const { error: upErr } = await sb.from(tableName).update(updates).eq('iid', iid);
      if (upErr) {
        alert('Error: ' + upErr.message);
        saveBtn.textContent = 'Save Changes';
      } else {
        window.location.href = `/teacher_edit?iid=${iid}&mode=view`;
      }
    };

    // Upload Logic
    groupsContainer.querySelectorAll('.upload-btn').forEach((btn: any) => {
      btn.onclick = () => {
        const field = btn.dataset.field;
        const fileInp = groupsContainer.querySelector(`input.hidden-file[data-field="${field}"]`) as HTMLInputElement;
        fileInp.click();
        fileInp.onchange = async () => {
          if (!fileInp.files?.[0]) return;
          btn.textContent = 'Uploading...';
          const formData = new FormData();
          formData.append('image', fileInp.files[0]);
          try {
            const res = await fetch(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, {
              method: 'POST',
              body: formData
            });
            const json = await res.json();
            if (json.success) {
              const url = json.data.url;
              const textInp = groupsContainer.querySelector(`input[name="${field}"]`) as HTMLInputElement;
              textInp.value = url;
              // Update thumb
              let thumb = btn.parentElement.querySelector('.row-thumb');
              if (!thumb) {
                thumb = document.createElement('img');
                thumb.className = 'row-thumb hover-preview';
                btn.parentElement.prepend(thumb);
              }
              thumb.src = url;
              thumb.dataset.url = url;
              btn.textContent = '📷 Upload';
              setupHoverPreview(); // Re-setup
            } else {
              alert('Upload failed');
              btn.textContent = '📷 Upload';
            }
          } catch (e) {
            alert('Error uploading');
            btn.textContent = '📷 Upload';
          }
        };
      };
    });
  }

  function setupHoverPreview() {
    const previewEl = document.getElementById('photo-preview')!;
    const previewImg = previewEl.querySelector('img')!;
    document.querySelectorAll('.hover-preview, #teacherPhoto').forEach((el: any) => {
      el.addEventListener('mouseenter', (e: any) => {
        const src = e.target.src || e.target.dataset.url;
        if (src) {
          previewImg.src = src;
          previewEl.style.display = 'block';
        }
      });
      el.addEventListener('mouseleave', () => {
        previewEl.style.display = 'none';
      });
    });
  }
  setupHoverPreview();
}
